/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';

import ContentFrame from '../../applicationFrame/ContentFrame';
import { ScrollAwareContext, ScrollAwareScroller, ScrollAwareAppNavStep } from '../../components/ScrollAware';
import { AppHeaderScrollableText } from '../../components/appNav/AppHeaderScrollable';
import InputWithLabel from '../../components/InputWithLabel';
import InputAlignedText from '../../components/InputAlignedText';
import InputErrorText from '../../components/InputErrorText';
import AppUtils from '../../../utilities/AppUtils';

import createFrequency from '../../../models/HealthEntryCompose/frequency';
import ContentWrapper from '../../components/ContentWrapper';
import { Card } from '../../../bphComponents/bundle';
import FlexRow from '../../components/FlexRow';
import HEAttributeFooter from './components/HEAttributeFooter';

import { useNavigate } from '../../hooks/useNavigate';
import RouteNames from '../../appAuthorized/RouteNames';
import { useHealthEntryForActiveProfile } from '../../hooks/useHealthEntry';
import { useFrequencyList } from '../../hooks/useMedli';
import HealthEntryModel from '../../../models/HealthEntry';
import Frequency, { frequencyPhraseKeys } from '../../../models/Frequency';

interface Props {
  match: any;
  intl: any;
}

const FrequencyFillInTheBlankText = (props) => {
  const { phraseKey, value, onChange, onBlur, onFocus, errorMessage, showInvalidError } = props;
  const textParts = props.intl.formatMessage({ id: phraseKey }, { digit1: 4 }).split('4');

  return (
    <Card.CardShape>
      <Card.CardContent>
        <FlexRow style={{ justifyContent: 'flex-start' }}>
          {
            textParts.map((t, i) => (
              <React.Fragment key={i}>
                {
                  (Boolean(t)) ? (
                    <InputAlignedText className="font-subhead font-color-gsDark" style={{ marginRight: 12 }}>{t}</InputAlignedText>
                  ) : null
                }
                {
                  (i < textParts.length - 1) ? (
                    <div style={{ width: 60, marginRight: 12 }}>
                      <InputWithLabel
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        onFocus={onFocus}
                        isInvalid={showInvalidError && Boolean(errorMessage)}
                        type={AppUtils.isIosDevice() ? 'text' : 'tel'}
                        name="frequency_times_value"
                        initialFocus={showInvalidError && !Boolean(value)}
                        noForcedHeight
                        noForcedLabel
                      />
                    </div>
                  ) : null
                }
              </React.Fragment>

            ))
          }
        </FlexRow>

        {errorMessage ? <InputErrorText>{errorMessage}</InputErrorText> : null}
      </Card.CardContent>
    </Card.CardShape>
  );
};
const IntlFrequencyFillInTheBlankText = injectIntl(FrequencyFillInTheBlankText);

const HealthEntryCustomFrequency: React.FC<Props> = (props) => {
  const healthEntryId = Number(props.match.params.healthEntryId);
  const groupName = props.match.params.groupName;

  const [, navActions] = useNavigate();
  const [, heActions] = useHealthEntryForActiveProfile(healthEntryId);
  const [, freqActions] = useFrequencyList();

  const peHealthEntry = heActions.getEditing();
  const frequencyData = HealthEntryModel.getFrequency(peHealthEntry);
  const selectedFrequency = freqActions.get(frequencyData.id) || {};
  const frequencyValue = selectedFrequency.group === groupName ? frequencyData.value : '';

  const [errorState, setErrorState] = React.useState({ isInvalid: !Boolean(frequencyValue), errorMessage: null });
  const [state, setState] = React.useState({
    isTimesFrequency: Frequency.isTimes(selectedFrequency.key) || !frequencyValue,
    value: frequencyValue,
  });

  const timesValue = state.isTimesFrequency ? state.value : '';
  const everyValue = state.isTimesFrequency ? '' : state.value;
  const hasValue = Boolean(frequencyValue);

  const onChangeValue = (isTimesFrequency, event) => {
    const value = event.target.value;
    const freq = createFrequency({});
    freq.setValue(value);

    setState({
      isTimesFrequency,
      value,
    });
    setErrorState({
      errorMessage: null,
      isInvalid: freq.isInvalid(),
    });
  };

  const onBlur = () => {
    if (state.value !== '') {
      const freq = createFrequency({});
      freq.setValue(state.value);
      setErrorState({
        ...errorState,
        errorMessage: freq.isInvalid() ? <FormattedMessage id="error.number-validator.pos-number-or-range.message" /> : null,
      });
    }
  };
  const onFocus = () => {
    if (state.isTimesFrequency) {
      setErrorState({
        ...errorState,
        errorMessage: null,
      });
    }
  };

  return (
    <div>
      <ScrollAwareContext>
        <ScrollAwareAppNavStep
          cancel={() => {
            navActions.goBackTo(RouteNames.healthEntryAmountAndFrequency(healthEntryId));
          }}
          submit={() => {

            if (errorState.isInvalid) { return; }
            const newHe = heActions.instantiateEditingHealthEntry();
            const phraseKey = Frequency.getFrequencyPhraseKey(groupName, state.value, state.isTimesFrequency);
            const fData = freqActions.getFromPhraseKey(phraseKey);
            newHe.frequency.setId(fData.id);
            newHe.frequency.setValue(state.value);
            heActions.updateEditing(newHe.data()).then(() => {
              navActions.goBackTo(RouteNames.healthEntryAmountAndFrequency(healthEntryId));
            });
          }}
          submitDisabled={errorState.isInvalid}
          color="white"
        />

        <ContentFrame>
          <ScrollAwareScroller>
            <AppHeaderScrollableText
              id="amtFreq.freqOnly.title.text"
            />
            <ContentWrapper>
              <div>
                <IntlFrequencyFillInTheBlankText
                  phraseKey={frequencyPhraseKeys[groupName].times.whole}
                  value={timesValue}
                  onChange={(e) => { onChangeValue(true, e); }}
                  showInvalidError={Boolean(state.isTimesFrequency)}
                  onBlur={onBlur}
                  onFocus={onFocus}
                  errorMessage={timesValue && errorState.errorMessage}
                />

                <Card.ExternalCardTitle>
                  <FormattedMessage id="amtFreq.or.label.text" />
                </Card.ExternalCardTitle>

                <IntlFrequencyFillInTheBlankText
                  phraseKey={frequencyPhraseKeys[groupName].every.whole}
                  value={everyValue}
                  onChange={(e) => { onChangeValue(false, e); }}
                  showInvalidError={!Boolean(state.isTimesFrequency)}
                  onBlur={onBlur}
                  onFocus={onFocus}
                  errorMessage={everyValue && errorState.errorMessage}
                />
              </div>
              {
                hasValue && (
                  <HEAttributeFooter
                    onDelete={() => {
                      const pendingHealthEntry = heActions.instantiateEditingHealthEntry();
                      pendingHealthEntry.frequency.clear();

                      heActions.apiUpdate(pendingHealthEntry.data()).then(() => {
                        heActions.updateEditing(pendingHealthEntry.data());
                        navActions.goBackTo(RouteNames.healthEntryAmountAndFrequency(healthEntryId));
                      });
                    }}
                    onToggleFlag={null}
                  />
                )
              }
            </ContentWrapper>
          </ScrollAwareScroller>
        </ContentFrame>
      </ScrollAwareContext>
    </div>
  );
};

export default HealthEntryCustomFrequency;
